import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoaderCircle, Calendar, Clock, ArrowRight, Search, MapPin, Bookmark } from "lucide-react";
import { Helmet } from "react-helmet-async";
import apiService from "../../services/api/apiServices";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [imgBasePath, setImgBasePath] = useState("");
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getBlogs();
      console.log("Blogs API Response:", response); // Debug log
      if (response.status && response.data) {
        const blogData = response.data.blogs || [];
        setBlogs(blogData);
        setFilteredBlogs(blogData);
        setImgBasePath(response.imgPath || "");
      } else {
        throw new Error("Failed to fetch blogs");
      }
    } catch (err) {
      console.error("Blog fetch error:", err);
      setError("Failed to fetch blogs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    const filtered = blogs.filter(blog =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.shortDesc.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBlogs(filtered);
  }, [searchTerm, blogs]);

  const handleBlogClick = (blog) => {
    navigate(`/blog/${blog.slug}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEstimatedReadTime = (shortDesc) => {
    const words = shortDesc.split(' ').length;
    const readTime = Math.max(1, Math.ceil(words / 200)); // Assume 200 words per minute
    return `${readTime} min read`;
  };

  // Component for alternating layout - first 4 blogs
  const AlternatingBlogCard = ({ blog, isReversed, index }) => (
    <article
      className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 group cursor-pointer transform hover:-translate-y-2`}
      onClick={() => handleBlogClick(blog)}
      style={{
        animationDelay: `${index * 150}ms`,
      }}
    >
      {/* Image Section */}
      <div className="lg:w-1/2 h-80 lg:h-96 relative overflow-hidden">
        <img
          src={`${imgBasePath}${blog.img.replace(/\.(jpeg|jpg|png)$/i, '')}.jpg`}
          alt={blog.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          onError={(e) => {
            e.target.src = `${imgBasePath}${blog.img}`;
            if (e.target.src === `${imgBasePath}${blog.img}`) {
              e.target.src = 'https://via.placeholder.com/600x400/f97316/ffffff?text=Amazing+Place';
              e.target.onerror = null;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Featured Badge */}
        <div className="absolute top-6 left-6">
          <div className="flex items-center bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
            <MapPin className="w-4 h-4 mr-2" />
            Featured Story #{index + 1}
          </div>
        </div>

        {/* Bookmark Icon */}
        <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors">
            <Bookmark className="w-5 h-5 text-orange-600" />
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center text-gray-500">
            <Calendar className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">{formatDate(blog.date)}</span>
          </div>
          <div className="flex items-center text-gray-500">
            <Clock className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">{getEstimatedReadTime(blog.shortDesc)}</span>
          </div>
          <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold">
            Travel Guide
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4 leading-tight group-hover:text-orange-600 transition-colors">
          {blog.title}
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-lg leading-relaxed mb-6">
          {blog.shortDesc}
        </p>

        {/* Call to Action */}
        <div className="flex items-center justify-between">
          <div className="flex items-center text-orange-500 font-semibold text-lg group-hover:text-orange-600 transition-colors">
            <span className="mr-3">Read Full Story</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
          </div>
        </div>
      </div>
    </article>
  );

  // Component for grid layout - remaining blogs
  const GridBlogCard = ({ blog, index }) => (
    <article
      className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border border-gray-100 group transform hover:-translate-y-3"
      onClick={() => handleBlogClick(blog)}
      style={{
        animationDelay: `${(index + 4) * 100}ms`,
      }}
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={`${imgBasePath}${blog.img.replace(/\.(jpeg|jpg|png)$/i, '')}.jpg`}
          alt={blog.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={(e) => {
            e.target.src = `${imgBasePath}${blog.img}`;
            if (e.target.src === `${imgBasePath}${blog.img}`) {
              e.target.src = 'https://via.placeholder.com/400x300/f97316/ffffff?text=Beautiful+Place';
              e.target.onerror = null;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Story Number */}
        <div className="absolute top-4 left-4">
          <span className="bg-white/95 backdrop-blur-sm text-orange-600 px-3 py-1 rounded-full text-sm font-bold shadow-sm">
            Story #{index + 5}
          </span>
        </div>

        {/* Bookmark */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors">
            <Bookmark className="w-4 h-4 text-orange-600" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Meta Info */}
        <div className="flex items-center justify-between text-gray-500 text-sm mb-4">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            <span>{formatDate(blog.date)}</span>
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            <span>{getEstimatedReadTime(blog.shortDesc)}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-orange-600 transition-colors line-clamp-2 leading-tight">
          {blog.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 leading-relaxed mb-5 line-clamp-3">
          {blog.shortDesc}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center text-orange-500 font-semibold group-hover:text-orange-600 transition-colors">
            <span className="mr-2">Read More</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
            <span className="text-xs text-gray-400 uppercase tracking-wide">Guide</span>
          </div>
        </div>
      </div>
    </article>
  );

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Travel Stories - Aaoghumen</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              className="animate-spin mx-auto mb-4"
              size={50}
              color={"#EA5722"}
              strokeWidth={2}
            />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Discovering Amazing Stories...</h3>
            <p className="text-gray-500">Loading the best travel destinations for you</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error Loading Stories - Aaoghumen</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center p-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 text-center border border-red-100">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-3xl">⚠️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Unable to Load Stories</h2>
              <p className="text-red-600 mb-8 text-lg">{error}</p>
              <button
                onClick={fetchBlogs}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Travel Stories & Destination Guides | Aaoghumen</title>
        <meta name="description" content="Discover amazing travel stories, destination guides, and hidden gems from around the world. Get inspired for your next adventure with our expert travel insights." />
        <meta name="keywords" content="travel stories, destination guides, travel blog, travel tips, hidden gems, travel inspiration, adventure stories" />
        <meta property="og:title" content="Travel Stories & Destination Guides | Aaoghumen" />
        <meta property="og:description" content="Discover amazing travel stories and destination guides from around the world." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-red-500 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                  ✨ Curated Travel Stories
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
                Discover
                <span className="block text-yellow-300">Amazing Places</span>
              </h1>
              <p className="text-xl sm:text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
                Uncover hidden gems, local secrets, and unforgettable experiences through our handpicked collection of travel stories and destination guides.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto relative mb-8">
                <div className="relative">
                  <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                  <input
                    type="text"
                    placeholder="Search destinations, stories, experiences..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-16 pr-6 py-5 rounded-2xl border-0 focus:outline-none focus:ring-4 focus:ring-white/30 bg-white/95 backdrop-blur-sm text-gray-700 placeholder-gray-500 text-lg shadow-2xl"
                  />
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex justify-center items-center gap-8 text-white/90">
                <div className="text-center">
                  <div className="text-3xl font-bold">{filteredBlogs.length}+</div>
                  <div className="text-sm opacity-80">Stories</div>
                </div>
                <div className="w-px h-12 bg-white/30"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold">50+</div>
                  <div className="text-sm opacity-80">Destinations</div>
                </div>
                <div className="w-px h-12 bg-white/30"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold">100k+</div>
                  <div className="text-sm opacity-80">Readers</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          {filteredBlogs.length === 0 && !loading ? (
            <div className="text-center py-20">
              <div className="bg-white rounded-3xl shadow-xl p-16 max-w-2xl mx-auto border border-gray-100">
                <div className="text-8xl mb-8">🔍</div>
                <h3 className="text-3xl font-bold text-gray-800 mb-6">No Stories Found</h3>
                <p className="text-gray-600 text-xl leading-relaxed">
                  {searchTerm 
                    ? `No stories match "${searchTerm}". Try searching for different destinations or topics.` 
                    : "No travel stories available at the moment. Check back soon for amazing content!"
                  }
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="mt-8 bg-orange-500 text-white px-8 py-3 rounded-xl hover:bg-orange-600 transition-colors font-semibold"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Search Results Info */}
              {searchTerm && (
                <div className="text-center mb-12">
                  <div className="inline-block bg-white rounded-2xl shadow-lg px-8 py-4 border border-gray-100">
                    <p className="text-gray-600 text-lg">
                      Found <span className="font-bold text-orange-600 text-2xl">{filteredBlogs.length}</span> stories for "{searchTerm}"
                    </p>
                  </div>
                </div>
              )}

              {/* First 4 Blogs - Alternating Layout */}
              {filteredBlogs.length > 0 && (
                <div className="space-y-16 mb-20">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                      Featured Travel Stories
                    </h2>
                    <p className="text-gray-600 text-lg">
                      Handpicked destinations that will inspire your next adventure
                    </p>
                  </div>
                  
                  {filteredBlogs.slice(0, 4).map((blog, index) => (
                    <AlternatingBlogCard
                      key={blog.slug}
                      blog={blog}
                      isReversed={index % 2 === 1}
                      index={index}
                    />
                  ))}
                </div>
              )}

              {/* Remaining Blogs - Grid Layout */}
              {filteredBlogs.length > 4 && (
                <>
                  <div className="text-center mb-12">
                    <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full font-semibold text-lg shadow-lg mb-4">
                      More Amazing Stories
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
                      Explore More Destinations
                    </h3>
                    <p className="text-gray-600">
                      Continue your journey with these incredible travel experiences
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {filteredBlogs.slice(4).map((blog, index) => (
                      <GridBlogCard
                        key={blog.slug}
                        blog={blog}
                        index={index}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Call to Action */}
        {filteredBlogs.length > 0 && (
          <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-red-500 text-white py-20 relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute inset-0">
              <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative max-w-6xl mx-auto text-center px-4">
              <div className="mb-6">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                  🚀 Ready for Adventure?
                </span>
              </div>
              
              <h2 className="text-3xl sm:text-5xl font-bold mb-6 leading-tight">
                Turn These Stories Into
                <span className="block text-yellow-300">Your Reality</span>
              </h2>
              
              <p className="text-xl sm:text-2xl opacity-90 mb-10 max-w-4xl mx-auto leading-relaxed">
                Let our expert travel guides help you plan the perfect trip to these amazing destinations with personalized recommendations
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button
                  onClick={() => navigate("/")}
                  className="bg-white text-orange-600 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
                >
                  🗺️ Plan Your Perfect Trip
                </button>
                <button
                  onClick={() => navigate("/onboarding")}
                  className="bg-orange-400/30 backdrop-blur-sm text-white border-2 border-white/30 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all"
                >
                  ✨ Get Personal Recommendations
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSS for animations and utility classes */}
        <style jsx>{`
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default Blog;